import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */
import React, { useCallback } from 'react';
import { Box, Grid, Skeleton } from '@mui/material';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useTypedTheme } from '@monorepo-web/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareFull } from '@fortawesome/free-solid-svg-icons';
import { Item } from './style';
import { helperFormat, FormatTypes, useIsMobile } from '@monorepo-web/utils';
/** Returns legend code (A, B, C, ...) based on the data index (0, 1, 2, ...) */
const getCode = function (index) {
    return String.fromCharCode(65 + (index % 26)).repeat(Math.floor(index / 26) + 1);
};
const gridItem = ({ index, id, value, color, onClick, disableCursor, }) => {
    return (_jsx(Grid, Object.assign({ item: true, xs: 6 }, { children: _jsx(Item, Object.assign({ onClick: onClick, style: { cursor: disableCursor ? 'auto' : 'pointer' } }, { children: id ? (_jsx(_Fragment, { children: _jsxs(Grid, Object.assign({ container: true, style: {
                        color: '#596579',
                        fontWeight: 500,
                        padding: '10px',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        lineHeight: 1.2,
                    } }, { children: [_jsxs(Grid, Object.assign({ item: true, xs: 9, sm: 10, style: { paddingRight: '8px' } }, { children: [_jsx(FontAwesomeIcon, { icon: faSquareFull, style: { color, marginRight: 4 } }), ' ', _jsxs("span", { children: [getCode(index), ". ", id] })] })), _jsx(Grid, Object.assign({ item: true, xs: 3, sm: 2 }, { children: _jsx(Box, Object.assign({ display: "flex", justifyContent: "flex-end" }, { children: _jsx("b", { children: value }) })) }))] })) })) : null })) }), `item-${index}`));
};
export function GraficoPizza({ data, height, onClickHandle, FormatTypeParam = FormatTypes.percentage, handleOnAnimationEnd, dispatchPDF, moneyPrefix, isLegendHorizontal, outerRadius = "85%", innerRadius = "52%", disableCursor = false, legendHeight = 130, }) {
    const theme = useTypedTheme();
    const isMobile = useIsMobile();
    const handleClick = useCallback((entry) => {
        if (onClickHandle)
            onClickHandle(entry);
    }, []);
    let smallSlice = false;
    const RADIAN = Math.PI / 180;
    const renderCustomizedLabel = (arg) => {
        const x = arg.cx + (arg.outerRadius + 10) * Math.cos(-arg.midAngle * RADIAN);
        const y = arg.cy + (arg.outerRadius + 10) * Math.sin(-arg.midAngle * RADIAN);
        if (arg.index == 0)
            smallSlice = false;
        // Se a slice anterior for considerada pequena, ela aparecerá normalmente; no entanto, as slices subsequentes não aparecerão.
        if (smallSlice)
            return;
        if (arg.percent < 0.02)
            smallSlice = true;
        return (_jsx("text", Object.assign({ x: x, y: y, fill: "black", textAnchor: x > arg.cx ? "start" : "end", dominantBaseline: "central" }, { children: getCode(arg.index) })));
    };
    if (isLegendHorizontal && height && isMobile)
        height += 100;
    isLegendHorizontal = isLegendHorizontal && !isMobile;
    const renderLegend = (props) => {
        const { payload } = props;
        if (payload.length % 2 !== 0) {
            payload.push({ id: "", value: "" });
        }
        return (_jsx(Grid, Object.assign({ container: true, style: {
                marginTop: isLegendHorizontal ? '' : 16,
                marginBottom: isLegendHorizontal ? 30 : '',
                fontSize: 12,
                width: isLegendHorizontal ? 360 : 'auto',
                height: isLegendHorizontal ? '' : legendHeight,
            }, spacing: { xs: 3, sm: 2.2, xl: 1 }, justifyContent: "space-evenly" }, { children: payload.map((entry, index) => {
                var _a;
                return (_jsx(React.Fragment, { children: gridItem({
                        index,
                        id: entry.id,
                        value: entry.value,
                        color: (_a = entry.color) !== null && _a !== void 0 ? _a : theme.chartColors[index % theme.chartColors.length],
                        onClick: () => handleClick(entry.id),
                        disableCursor
                    }) }, index));
            }) })));
    };
    // Layout da legenda
    let legendLayout = isLegendHorizontal
        ? "vertical"
        : "horizontal";
    let legendVAlign = isLegendHorizontal
        ? "middle"
        : "bottom";
    let legendHAlign = isLegendHorizontal
        ? "right"
        : "center";
    const getLegendPayload = () => data === null || data === void 0 ? void 0 : data.map((item, index) => {
        var _a;
        return ({
            id: item.name,
            value: helperFormat(item.value, FormatTypeParam, 2, moneyPrefix !== null && moneyPrefix !== void 0 ? moneyPrefix : "R$"),
            color: (_a = item.color) !== null && _a !== void 0 ? _a : theme.chartColors[index % theme.chartColors.length],
        });
    });
    return data && data.length > 0 ? (_jsxs("div", Object.assign({ style: { display: "flex", alignItems: "center" } }, { children: [_jsx(ResponsiveContainer, Object.assign({ width: "100%", height: height }, { children: _jsxs(PieChart, Object.assign({ style: { margin: "auto" } }, { children: [_jsx(Pie, Object.assign({ data: data, dataKey: "value", startAngle: 90, endAngle: -270, outerRadius: dispatchPDF ? 180 : outerRadius, innerRadius: dispatchPDF ? 110 : innerRadius, fill: "green", cx: "50%", cy: "50%", labelLine: false, label: renderCustomizedLabel, onClick: (data) => {
                                if (data && data.name) {
                                    handleClick(data.name);
                                }
                            }, onAnimationEnd: handleOnAnimationEnd }, { children: data.map((entry, index) => {
                                var _a;
                                return (_jsx(Cell, Object.assign({ style: { outline: "none" } }, (!disableCursor && { cursor: "pointer" }), { fill: (_a = entry.color) !== null && _a !== void 0 ? _a : theme.chartColors[index % theme.chartColors.length] }), `cell-${index}`));
                            }) })), _jsx(Tooltip, { formatter: (value, name) => [
                                helperFormat(value, FormatTypeParam, 2, moneyPrefix !== null && moneyPrefix !== void 0 ? moneyPrefix : "R$"),
                                name,
                            ] }), dispatchPDF || isLegendHorizontal ? null : (_jsx(Legend, { payload: getLegendPayload(), content: (props) => renderLegend(props), layout: legendLayout, verticalAlign: legendVAlign, align: legendHAlign }))] })) })), dispatchPDF || !isLegendHorizontal ? null : (_jsx("div", Object.assign({ style: { width: "100%", display: "flex", justifyContent: "center" } }, { children: renderLegend({
                    payload: getLegendPayload(),
                }) })))] }))) : (_jsx(Skeleton, { variant: "circular", height: height, width: "100%" }));
}
